import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CheckIcon, ChevronsUpDownIcon, FolderIcon } from 'lucide-react';

import { qk } from '@/api/query-keys';
import type { Me } from '@/api/schemas';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { Workspace } from '@/lib/workspace';
import { cn } from '@/lib/utils';

/**
 * Org and project switching.
 *
 * A user may belong to N orgs and switch without logging out (FR-AUTH-5), and
 * the switch is plain navigation because `project_id` travels in the URL path
 * and the org is derived from it server side. There is no client held "current
 * org", so there is no stale membership window to keep consistent.
 *
 * On switch the outgoing project's cached queries are removed rather than left
 * to expire. Every project scoped key starts `['project', projectId]`, so this
 * is one call and it is exact: the new project can never render the previous
 * one's numbers, not even for a frame while its own request is in flight.
 */
export function ProjectSwitcher({ me, workspace }: { me: Me; workspace: Workspace }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const multiOrg = me.orgs.length > 1;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-9 max-w-[16rem] gap-2 px-2">
          <FolderIcon className="size-4 shrink-0 opacity-70" aria-hidden />
          <span className="truncate text-sm font-medium">{workspace.project.name}</span>
          <ChevronsUpDownIcon className="size-3.5 shrink-0 opacity-50" aria-hidden />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="min-w-[16rem]">
        {me.orgs.map((org, orgIndex) => (
          <div key={org.org_id}>
            {orgIndex > 0 ? <DropdownMenuSeparator /> : null}
            {multiOrg ? (
              <DropdownMenuLabel className="flex items-center justify-between gap-2">
                <span className="truncate">{org.name}</span>
                <span className="text-[0.65rem] uppercase">{org.role}</span>
              </DropdownMenuLabel>
            ) : null}
            {org.projects.length === 0 ? (
              <DropdownMenuItem disabled>No projects in this organisation</DropdownMenuItem>
            ) : (
              org.projects.map((project) => {
                const active = project.project_id === workspace.project.project_id;
                return (
                  <DropdownMenuItem
                    key={project.project_id}
                    onSelect={() => {
                      if (active) return;
                      queryClient.removeQueries({
                        queryKey: qk.project(workspace.project.project_id),
                      });
                      void navigate(`/p/${encodeURIComponent(project.project_id)}`);
                    }}
                  >
                    <CheckIcon
                      className={cn('size-4 shrink-0', active ? 'opacity-100' : 'opacity-0')}
                      aria-hidden
                    />
                    <span className="truncate">{project.name}</span>
                  </DropdownMenuItem>
                );
              })
            )}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
